import type { JsonSchema } from "../types.js";

export const EPISODE_TITLE_SCHEMA: JsonSchema = {
  type: "object",
  required: ["episode_titles"],
  additionalProperties: false,
  properties: {
    episode_titles: {
      type: "array",
      items: {
        type: "object",
        required: ["episode", "title"],
        additionalProperties: false,
        properties: { episode: { type: "integer" }, title: { type: "string" } },
      },
    },
  },
};

export const SYNOPSIS_SCHEMA: JsonSchema = {
  type: "object",
  required: ["synopsis"],
  additionalProperties: false,
  properties: { synopsis: { type: "string" } },
};

export const SCRIPT_SYNOPSIS_SCHEMA: JsonSchema = {
  type: "object",
  required: ["synopsis", "theme", "logline", "style", "main_characters"],
  additionalProperties: false,
  properties: {
    synopsis: { type: "string" },
    theme: { type: "string" },
    logline: { type: "string" },
    style: { type: "string" },
    main_characters: {
      type: "array",
      items: {
        type: "object",
        required: ["name", "role"],
        additionalProperties: false,
        properties: { name: { type: "string" }, role: { type: "string" } },
      },
    },
  },
};

export const ASSET_AUDIT_SCHEMA: JsonSchema = {
  type: "object",
  required: ["issues"],
  additionalProperties: false,
  properties: {
    issues: {
      type: "array",
      items: {
        type: "object",
        required: [
          "type",
          "asset_ids",
          "state_ids",
          "names",
          "evidence",
          "recommendation",
          "suggested_survivor",
          "action",
          "confidence",
        ],
        additionalProperties: false,
        properties: {
          type: {
            type: "string",
            enum: [
              "duplicate_actor",
              "duplicate_location",
              "duplicate_prop",
              "duplicate_state",
              "suspicious_singleton_state",
              "unused_state",
              "needs_human_review",
            ],
          },
          asset_ids: { type: "array", items: { type: "string" } },
          state_ids: { type: "array", items: { type: "string" } },
          names: { type: "array", items: { type: "string" } },
          evidence: { type: "string" },
          recommendation: { type: "string" },
          suggested_survivor: { type: ["string", "null"] },
          action: { type: "string", enum: ["merge", "delete", "review"] },
          confidence: { type: "string", enum: ["high", "medium", "low"] },
        },
      },
    },
  },
};

const STATE_REF_SCHEMA: JsonSchema = {
  type: "array",
  items: {
    type: "object",
    required: ["asset_id", "state_id"],
    additionalProperties: false,
    properties: { asset_id: { type: "string" }, state_id: { type: ["string", "null"] } },
  },
};

export const STATE_BINDING_SCHEMA: JsonSchema = {
  type: "object",
  required: ["scenes"],
  additionalProperties: false,
  properties: {
    scenes: {
      type: "array",
      items: {
        type: "object",
        required: ["scene_id"],
        additionalProperties: false,
        properties: {
          scene_id: { type: "string" },
          actors: {
            ...STATE_REF_SCHEMA,
            items: {
              type: "object", required: ["actor_id", "state_id"], additionalProperties: false,
              properties: { actor_id: { type: "string" }, state_id: { type: ["string", "null"] } },
            },
          },
          locations: {
            ...STATE_REF_SCHEMA,
            items: {
              type: "object", required: ["location_id", "state_id"], additionalProperties: false,
              properties: { location_id: { type: "string" }, state_id: { type: ["string", "null"] } },
            },
          },
          props: {
            ...STATE_REF_SCHEMA,
            items: {
              type: "object", required: ["prop_id", "state_id"], additionalProperties: false,
              properties: { prop_id: { type: "string" }, state_id: { type: ["string", "null"] } },
            },
          },
          state_changes: {
            type: "array",
            items: {
              type: "object",
              required: ["action_index", "target_kind", "target_id", "to_state_id"],
              additionalProperties: false,
              properties: {
                action_index: { type: "integer" },
                target_kind: { type: "string", enum: ["actor", "location", "prop"] },
                target_id: { type: "string" },
                from_state_id: { type: ["string", "null"] },
                to_state_id: { type: "string" },
                effective_from: { type: "string", enum: ["before_action", "after_action"] },
              },
            },
          },
        },
      },
    },
  },
};

const DEFAULT_LOOK_SCHEMA: JsonSchema = {
  type: "object",
  required: ["name", "description"],
  additionalProperties: false,
  properties: {
    name: { type: "string", enum: ["常态"], description: "实体首次登记时的默认造型名，固定为常态" },
    description: { type: "string", description: "默认造型的可见外观，只写外貌、服饰、材质、颜色、布景或形制，非空" },
  },
};

// Serial per-window asset/look Delta (§ serial, ingest.episode-assets). Native
// structured output: the model returns this object directly, so canonical names
// never get mangled by markdown delimiters. `name` holds ONLY the pure canonical
// name; every translation / past-life / alt name goes in `aliases` — the split
// the markdown `名 | 名 | 描述` format could not enforce.
export const SERIAL_DELTA_SCHEMA: JsonSchema = {
  type: "object",
  required: ["newEntities", "merges", "looks", "lookChanges", "subEvents", "summary"],
  additionalProperties: false,
  properties: {
    newEntities: {
      type: "array",
      description: "本段新登记的实体(人物/场景/道具)。name=canonical，aliases 列全这段里它的所有其他叫法",
      items: {
        type: "object",
        required: ["kind", "name", "description", "aliases", "defaultLook"],
        additionalProperties: false,
        properties: {
          kind: { type: "string", enum: ["actor", "location", "prop"] },
          name: { type: "string", description: "纯 canonical 名，不含括号/斜杠/译名夹注/对白标记" },
          description: { type: "string", description: "一句话说明这个实体本身是谁/什么身份/什么东西，非空；人物只写身份、角色和关系，不写服装造型" },
          aliases: { type: "array", items: { type: "string" }, description: "这段里该实体的所有其他叫法：别名/译名/前世名/短名全名/对白说话人在正文里的写法，逐个单列(纯名，不带对白/情绪标记)" },
          defaultLook: DEFAULT_LOOK_SCHEMA,
        },
      },
    },
    merges: {
      type: "array",
      description: "本段里某个尚未登记的称呼，其实是某个已登记实体的别名",
      items: {
        type: "object",
        required: ["from", "into"],
        additionalProperties: false,
        properties: {
          from: { type: "string", description: "本段这个称呼(尚未作为独立实体登记过)" },
          into: { type: "string", description: "它所指的已登记实体的 canonical 名" },
        },
      },
    },
    looks: {
      type: "array",
      description: "默认造型之外、需要单独复现的新增造型",
      items: {
        type: "object",
        required: ["entity", "name", "aliases", "description"],
        additionalProperties: false,
        properties: {
          entity: { type: "string", description: "实体 canonical 名" },
          name: { type: "string", description: "造型名，必须中文，英文专名先译成中文" },
          aliases: { type: "array", items: { type: "string" }, description: "同义造型名" },
          description: { type: "string", description: "这套外观一句话" },
        },
      },
    },
    lookChanges: {
      type: "array",
      description: "造型变化：只在某实体的造型从这一场起发生变化时标一条；不变的场不用标(系统自动沿用)",
      items: {
        type: "object",
        required: ["scene", "entity", "look"],
        additionalProperties: false,
        properties: {
          scene: { type: "string", description: "变化发生的场景 id，如 scn_012" },
          entity: { type: "string", description: "实体 canonical 名" },
          look: { type: "string", description: "从这场起穿的造型名(必须是已在 looks 里注册过的档位)" },
        },
      },
    },
    subEvents: { type: "array", items: { type: "string" }, description: "本段关键剧情原子，每条一句话" },
    summary: { type: "string", description: "本段一句话梗概" },
  },
};

// Serial VIDEO-pass structured output (§10.2, ingest.video-serial). Replaces the
// prior "emit one markdown doc → parseSerialMd" contract: Gemini returns THIS object
// directly (responseMimeType application/json + responseJsonSchema). It carries
// both the source-faithful storyboard screenplay (scenes → visual/performance/sound anchors) and incremental registry
// maintenance (new_entities / enrich / canonicalize) + open threads. Mirrors the
// SerialOutput interface, with environment flattened to space/time. `state`/`states`
// descriptions forbid parens/slashes at the source — the drift that fragmented one
// outfit into 「黑丝短裙（身穿收银短袖」/「黑丝短裙/绷带短裙」under the md format —
// with a deterministic clean pass in runVideoSerial as backstop.
const KIND3 = { type: "string", enum: ["actor", "location", "prop"] } as const;
const STATE_ITEM = { type: "string", description: "造型档位名，只填名字本身；禁止括号、斜杠、逗号或描述性文字" } as const;
const DESCRIBED_STATE_ITEM = {
  type: "object",
  required: ["name", "description"],
  additionalProperties: false,
  properties: {
    name: STATE_ITEM,
    description: {
      type: "string",
      description: "该状态可复现的持续可见外观，非空。人物综合多个清晰镜头并从头到脚写该造型特有的妆发头饰、颈饰、外套、上装、下装/连体衣裙、袜类、鞋履、手套和饰品；每件可见单品写颜色、材质/光泽/纹理、版型松紧、领袖结构、开合、腰线、长度、下摆、图案文字、滚边金属件、叠穿关系与所在位置；持续破损污渍写部位、范围和颜色。只省略看不见或不能确认的项，不用一句笼统服装概括，不从局部补造全身。地点和道具写相对常态的持续外观差异。只写画面事实，不写动作、姿态、情绪、心理或剧情评价",
    },
  },
} as const;
const IDENTITY_PROFILE = {
  type: "object",
  required: ["apparent_gender", "apparent_age", "face_shape", "brows_eyes", "nose", "mouth", "skin_features", "hair", "build", "persistent_marks", "voice"],
  additionalProperties: false,
  properties: {
    apparent_gender: { type: "string", description: "画面呈现的性别；无法确认填空串" },
    apparent_age: { type: "string", description: "可见年龄段，使用窄而审慎的范围，如『约25-30岁』；无法确认填空串" },
    face_shape: { type: "string", description: "脸部轮廓、颧骨、下颌与下巴的客观形态；不写审美、气质、表情或剧情形态" },
    brows_eyes: { type: "string", description: "眉形、眉色、眼型、眼距、单双眼皮及眼尾的静态形态；不写目光、神情、情绪、妆容、隐形眼镜或变身颜色" },
    nose: { type: "string", description: "鼻梁、鼻头、鼻翼的客观形态" },
    mouth: { type: "string", description: "静止时的唇厚薄、唇峰、嘴角结构和口周特征；不写说话动作、表情或唇妆" },
    skin_features: { type: "string", description: "稳定肤色、面部纹理、痣、雀斑、旧疤等跨场景可见特征；不写临时伤痕、血迹、污渍或妆容" },
    hair: { type: "string", description: "稳定发色、发际线、长度、卷直和常态发型；不写假发、变身形态或一次性造型，临时差异写入 states" },
    build: { type: "string", description: "可由画面确认的身高相对关系、肩宽、骨架和体型；不写姿态、动作、气质或审美词" },
    persistent_marks: { type: "array", items: { type: "string" }, description: "纹身、旧疤、痣、胎记、固定穿孔等不可随换装摘除的身体标记；不写耳环、项链、眼镜等配饰，没有填空数组" },
    voice: { type: "string", description: "从清晰原声可确认的音高、音色、语速、口音或稳定发声质感；不写当下情绪、态度或角色性格，听不清填空串" },
  },
} as const;
export const VIDEO_SERIAL_SCHEMA: JsonSchema = {
  type: "object",
  required: ["synopsis", "scenes", "registry_delta", "open_threads"],
  additionalProperties: false,
  properties: {
    synopsis: { type: "string", description: "本集剧情梗概，按发生顺序，200-300 字纯文本" },
    scenes: {
      type: "array",
      description: "本集所有场景单元，按时间顺序排列。场内尽量忠实还原原片分镜：每次切镜、连续镜头内的摄影变化、可见表演和声音事件按实际时间交错保留，重复镜头也不去重；地点、明显时段或叙事线变化时拆场，普通切镜不单独拆 scene；同一连续场内的人物造型变化保留在同一场；跨地点蒙太奇拆成多个短场",
      items: {
        type: "object",
        required: ["space", "time", "timeRange", "location", "characters", "props", "shots", "voices"],
        additionalProperties: false,
        properties: {
          space: { type: "string", description: "内景填「内」/外景填「外」；不确定填空串" },
          time: {
            type: "string",
            enum: ["日", "夜", "晨", "昏"],
            description: "本场必须选择一个时段：白天/日间填「日」，夜晚/夜间填「夜」，清晨/黎明填「晨」，黄昏/傍晚填「昏」。结合照明、作息、天空和剧情上下文判断，不得留空",
          },
          timeRange: { type: "string", description: "本场起止时间 MM:SS-MM:SS；SS 必须为 00-59，整分钟写成下一分钟的 :00，禁止写 :60" },
          location: { type: "string", description: "本场唯一稳定主空间地点名，沿用规范名；客厅/卧室/卫生间/大厅/广场/舞台/后台分别建地点，相关空间用『豪宅·客厅』式命名" },
          characters: {
            type: "array",
            description: "本场在场的具体单人角色（群众不列）。同一角色只能出现一次；其 states 填本场出现过的全部稳定造型/状态集合",
            items: {
              type: "object",
              required: ["name", "states"],
              additionalProperties: false,
              properties: {
                name: { type: "string", description: "角色规范名" },
                states: { type: "array", items: STATE_ITEM, description: "本场该角色出现过的全部造型/状态名集合。每个状态是整套着装档位名，只填名字本身；禁止括号、斜杠、逗号或描述文字（错误：『黑丝短裙（身穿收银短袖）』『黑丝短裙/绷带短裙』；正确：『黑丝短裙』）。同一套造型沿用已知设定里的既有名；没换装或看不清填空数组。不得为同一角色拆成多条记录" },
                // Retired experiment: identity_evidence/state_evidence used to
                // be requested here. Evidence is now a deterministic post-ingest
                // sidecar derived from final Action timestamps.
              },
            },
          },
          props: { type: "array", items: { type: "string" }, description: "本场在场的关键道具名" },
          shots: {
            type: "array",
            description: "本场独立的逐镜清单，与声音分开盘点。原片每次切镜都必须有一条，重复镜头也不去重；连续镜头内的摄影或表演变化另增一条。不因为台词连续或剧情目的相同而省略分镜",
            items: {
              type: "object",
              required: ["ts", "camera", "visual"],
              additionalProperties: false,
              properties: {
                ts: { type: "string", description: "本镜头或镜内变化真正开始的时间戳 MM:SS（分:秒）；SS 必须为 00-59，整分钟写成下一分钟的 :00，禁止写 :60" },
                camera: { type: "string", description: "本镜头可确认的切换关系、景别、角度、机位、运镜、构图、焦点和画面主体；只写原片能确认的摄影事实，不推测镜头参数或拍摄意图" },
                visual: { type: "string", description: "本镜头实际可见的环境、人物占位与站位、姿态、面向、视线、表情、接触、动作对象、道具和结果；只写画面事实，不用情绪、态度或心理概括" },
              },
            },
          },
          voices: {
            type: "array",
            description: "本场独立的声音清单。完整保留出镜对白、画外对白、角色内心独白/自述、客观旁白、电话/广播/设备声和群体声。台词跨镜时按自然语句拆分，不得把跨多个镜头的多句台词合并成一条",
            items: {
              type: "object",
              required: ["kind", "speaker", "parenthetical", "ts", "content"],
              additionalProperties: false,
              properties: {
                kind: { type: "string", enum: ["dlg", "think"], description: "dlg=人物对他人说出的声音或客观旁白；think=音轨实际包含、明确属于某角色的内心独白/第一人称自述" },
                speaker: { type: "string", description: "说话人规范名；客观叙述者或身份不明旁白用『broadcast:旁白』，广播用『broadcast:名』，无法区分成员的群体共同发声用『group:群体名』" },
                parenthetical: { type: "string", description: "可确认的发声方式、声音来源，或与台词同时发生的短促可见表演；没有则填空串。摄影调度和影响连续性的动作属于 shots，不写在这里；不用抽象情绪、态度或心理标签" },
                ts: { type: "string", description: "本段声音真正开始的时间戳 MM:SS（分:秒）；SS 必须为 00-59，整分钟写成下一分钟的 :00，禁止写 :60" },
                content: { type: "string", description: "逐字保留音轨实际语言和夹杂语言，不翻译、不改写、不用翻译字幕替换外语原声" },
              },
            },
          },
        },
      },
    },
    registry_delta: {
      type: "object",
      description: "对已知设定的增量维护。已有且无新信息的实体一个字都别写",
      required: ["new_entities", "enrich", "canonicalize"],
      additionalProperties: false,
      properties: {
        new_entities: {
          type: "array",
          description: "本集首次出现、且已知设定里没有的实体。必须覆盖 scenes.location、scenes.characters、scenes.props 和人物 voices.speaker 中所有无法解析到已知设定或 canonicalize 目标的名称；只登记具体单人角色、稳定地点和关键道具，群众不登记",
          items: {
            type: "object",
            required: ["name", "kind", "status", "importance", "recognition_anchors", "identity_profile", "appearance", "role", "states", "aliases"],
            additionalProperties: false,
            properties: {
              name: { type: "string", description: "规范名；认不出且无本名的人用『稳定外观特征+年龄段+性别』构成不含姓的描述名，绝不编带姓假名" },
              kind: KIND3,
              status: { type: "string", enum: ["canonical", "provisional"], description: "canonical=身份已确认；provisional=认不出/未点名，待后续用直接命名证据升级规范名" },
              importance: { type: "string", enum: ["featured", "background"] },
              recognition_anchors: { type: "array", items: { type: "string" }, description: "人物填写 3-6 条跨集重认锚点，每条组合至少两个可在 identity_profile 找到依据的稳定特征；禁止服装、号码、帽子、眼镜/配饰、妆容、表情动作、临时伤痕污渍、剧情身份能力关系、变身形态或场景物件；地点/道具填空数组" },
              identity_profile: IDENTITY_PROFILE,
              appearance: { type: "string", description: "地点/道具的稳定可见描述，人物填空串。地点写空间结构、材质、色彩和固定陈设；道具写形制、材质、颜色和尺寸关系" },
              role: { type: "string", description: "实体的身份、关系或用途，非空；只写原片可确认的设定" },
              states: { type: "array", items: DESCRIBED_STATE_ITEM, description: "本集该实体出现的持久视觉状态。每项同时给出简洁稳定状态名与从多个清晰镜头汇总的可复现详细描述；没有独立状态填空数组" },
              aliases: { type: "array", items: { type: "string" }, description: "本集见到的其它称呼" },
            },
          },
        },
        enrich: {
          type: "array",
          description: "对已知设定里已有实体的补充/更正，只写发生变化的字段；无变化的字段填空串/空数组，整体无变化的实体不要出现",
          items: {
            type: "object",
            required: ["name", "add_recognition_anchors", "identity_profile_patch", "appearance", "role", "add_aliases", "add_states"],
            additionalProperties: false,
            properties: {
              name: { type: "string", description: "已知实体的规范名" },
              add_recognition_anchors: { type: "array", items: { type: "string" }, description: "本集新增、且每个词都能由 identity_profile 稳定字段支持的高区分度人物重认锚点；禁止服装配饰、表情动作、临时外观、剧情设定和变身形态；没有填空数组" },
              identity_profile_patch: IDENTITY_PROFILE,
              appearance: { type: "string", description: "地点/道具更准确的稳定外观；人物或无更新填空串" },
              role: { type: "string", description: "更明确的身份；无更新填空串" },
              add_aliases: { type: "array", items: { type: "string" }, description: "本集新出现的称呼" },
              add_states: { type: "array", items: DESCRIBED_STATE_ITEM, description: "本集首次出现的新状态，或本集从更多角度获得了更清晰细节的既有状态；既有状态逐字沿用其 name，以同名项补全或更新详细 description，不另建近义状态" },
            },
          },
        },
        canonicalize: {
          type: "array",
          description: "仅凭本集直接出现的书面正式姓名或台词本名，把已知人物的描述名、简称或不完整姓名升级为更准确的规范名；剧情推断不能单独作为改名证据",
          items: {
            type: "object",
            required: ["from_name", "canonical_name", "evidence"],
            additionalProperties: false,
            properties: {
              from_name: { type: "string", description: "已知设定中的当前规范名或既有别名" },
              canonical_name: { type: "string", description: "书面正式姓名或台词直接说出的本名" },
              evidence: { type: "string", description: "直接命名证据，必须包含 MM:SS 时间戳以及画面原文或听到的原话" },
            },
          },
        },
      },
    },
    open_threads: {
      type: "object",
      required: ["opened", "closed"],
      additionalProperties: false,
      properties: {
        opened: { type: "array", description: "本集新开、尚未交代结果的悬念/关系变化", items: { type: "object", required: ["text"], additionalProperties: false, properties: { text: { type: "string" } } } },
        closed: { type: "array", description: "本集交代了结果、要关闭的已知线索 id（如 t3_1）", items: { type: "object", required: ["ref"], additionalProperties: false, properties: { ref: { type: "string" } } } },
      },
    },
  },
};

// Name-fix repair (§ ingest.name-fix): for each body name that resolved to no
// registry entity, the model either merges it into an existing canonical or
// declares it a genuinely new entity. Same two acts as the serial pass, no scenes.
export const NAME_FIX_SCHEMA: JsonSchema = {
  type: "object",
  required: ["merges", "newEntities"],
  additionalProperties: false,
  properties: {
    merges: {
      type: "array",
      description: "未登记名字其实是某已登记实体的别名",
      items: {
        type: "object",
        required: ["from", "into"],
        additionalProperties: false,
        properties: {
          from: { type: "string", description: "这个未登记的名字" },
          into: { type: "string", description: "它所指的已登记实体 canonical 名" },
        },
      },
    },
    newEntities: {
      type: "array",
      description: "确实是全新实体的名字，各登记一条",
      items: {
        type: "object",
        required: ["kind", "name", "description", "aliases", "defaultLook"],
        additionalProperties: false,
        properties: {
          kind: { type: "string", enum: ["actor", "location", "prop"] },
          name: { type: "string", description: "纯 canonical 名" },
          description: { type: "string", description: "一句话身份，非空；人物不写服装造型" },
          aliases: { type: "array", items: { type: "string" }, description: "其他叫法" },
          defaultLook: DEFAULT_LOOK_SCHEMA,
        },
      },
    },
  },
};
